import {
  AppendIDOrIDs,
  CallbackSuccessHandler,
  CollectionOpts,
  DefinitionForType,
  Resource,
  ResourceDefinitions,
  ResourceType,
  isLoadingStatus,
  useJsonAPI,
  useResource,
} from 'json-api-hooks';
import React, { ReactNode } from 'react';
import { useForm } from 'react-hook-form';

import { stringToHash } from '../../lib/string';
import { isString } from '../../lib/typeCheckers';
import { CAResource } from '../../models';
import { LoadingRow } from '../Loading';

import Form from './Form';
import getDefaultValue from './lib/getDefaultValue';
import useFormSubmit from './lib/useFormSubmit';
import { InputType } from './types';

export type AppendAttributes<Attributes> = {
  [Renamed in keyof Attributes as `${string & Renamed}Attributes`]: Attributes[Renamed];
};
type StartsWithKey<K extends string> = `${K}${string}`;

export interface FormFieldOption {
  key: string;
  label: ReactNode;
}

export type FormFieldAttribute<T extends ResourceType | unknown> = T extends ResourceType
  ?
      | 'id'
      | keyof DefinitionForType<T>['attributes']
      | keyof AppendIDOrIDs<DefinitionForType<T>['relationships']>
      | StartsWithKey<keyof AppendAttributes<DefinitionForType<T>['relationships']>>
  : 'id';

export interface ResourceFieldAttributes<T extends ResourceType | void> {
  formValues: Record<T extends void ? string : FormFieldAttribute<T>, string>;
  resource?: T extends void ? Resource : DefinitionForType<Extract<T, keyof ResourceDefinitions>>;
  session: CAResource;
}

export interface FormFieldDefinition<T extends ResourceType | void = void> {
  aspectRatio?: number;
  attribute: T extends void ? string : FormFieldAttribute<T>;
  autoFocus?: boolean;
  description?: string;
  helper?: string;
  if?: (props: ResourceFieldAttributes<T>) => boolean | undefined;
  label?: string;
  multiple?: boolean;
  maxInclusive?: number;
  minInclusive?: number;
  nestedFields?: FormFieldDefinition[];
  options?: FormFieldOption[];
  placeholder?: string;
  prefix?: string;
  required?: boolean;
  resourceOpts?: CollectionOpts | ((props: ResourceFieldAttributes<T>) => CollectionOpts);
  resourceType?: ResourceType;
  type: InputType;
}

export interface ResourceFormProps<T extends ResourceType> {
  defaultValues?: Record<string, unknown>;
  description?: string;
  fields: Array<FormFieldDefinition<T> | undefined>;
  id?: string;
  submitLabel?: string;
  onClose?: () => void;
  onSuccess?: CallbackSuccessHandler<T>;
  type: T;
}

const useDefaultDates = <T extends ResourceType>(
  defaultValues: Record<string, unknown> | undefined,
  resource: DefinitionForType<T> | undefined,
  fields: Array<FormFieldDefinition<T> | undefined>,
  storageKeyPrefix: string,
) => {
  const { router, store } = useJsonAPI();

  return (fields.filter(Boolean) as FormFieldDefinition[]).reduce((values, formField) => {
    const storageKey = storageKeyPrefix ? `${storageKeyPrefix}-${formField.attribute}` : undefined;
    const value =
      defaultValues?.[formField.attribute] ?? getDefaultValue(store, router, resource, formField, storageKey);

    return { ...values, [formField.attribute]: value };
  }, {});
};

const ResourceForm = <T extends ResourceType>({
  defaultValues: defaultValuesProp,
  description,
  fields,
  id,
  onClose,
  onSuccess,
  submitLabel,
  type,
}: ResourceFormProps<T>) => {
  const [resource, status] = useResource({ id, type });
  const resourceIsLoading = isLoadingStatus(status);
  const storageKey = `${type}-${resource?.id ?? 'new'}-${stringToHash(JSON.stringify(defaultValuesProp))}`;
  const defaultValues = useDefaultDates(defaultValuesProp, resource, fields, storageKey);
  const methods = useForm({ defaultValues });
  const [handleFormSubmit, loading, clearStorage] = useFormSubmit(
    type,
    id,
    fields as FormFieldDefinition[],
    methods.handleSubmit,
    methods.setError,
    onSuccess as CallbackSuccessHandler,
    storageKey,
  );
  const handleClose = onClose
    ? () => {
        clearStorage();
        onClose();
      }
    : undefined;

  if (isString(id) && resourceIsLoading) {
    return <LoadingRow />;
  }

  return (
    <Form
      defaultValues={defaultValues}
      description={description}
      fields={fields as Array<FormFieldDefinition | undefined>}
      loading={loading}
      methods={methods}
      resource={resource}
      storageKey={storageKey}
      submitLabel={submitLabel ?? (id ? 'Bijwerken' : 'Toevoegen')}
      onClose={handleClose}
      onSubmit={handleFormSubmit}
    />
  );
};

export default ResourceForm;
